import type { DailyStatPoint } from '../types';

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fromIsoDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00.000Z`);
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

export function addDays(dateStr: string, days: number): string {
  const d = fromIsoDate(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return toIsoDate(d);
}

export interface IsoWeek {
  isoYear: number;
  isoWeek: number;
}

/** ISO 8601 week (Monday-start, week 1 contains the year's first Thursday). */
export function getIsoWeek(dateStr: string): IsoWeek {
  const d = fromIsoDate(dateStr);
  const dayNum = (d.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // nearest Thursday
  const isoYear = d.getUTCFullYear();
  const jan4 = fromIsoDate(`${isoYear}-01-04`);
  const jan4DayNum = (jan4.getUTCDay() + 6) % 7;
  const firstThursday = new Date(jan4);
  firstThursday.setUTCDate(jan4.getUTCDate() - jan4DayNum + 3);
  const isoWeek = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return { isoYear, isoWeek };
}

export function isoWeekToRange(isoYear: number, isoWeek: number): { from: string; to: string } {
  const jan4 = fromIsoDate(`${isoYear}-01-04`);
  const jan4DayNum = (jan4.getUTCDay() + 6) % 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4DayNum);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (isoWeek - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { from: toIsoDate(monday), to: toIsoDate(sunday) };
}

export function shiftIsoWeek(isoYear: number, isoWeek: number, deltaWeeks: number): IsoWeek {
  const { from } = isoWeekToRange(isoYear, isoWeek);
  return getIsoWeek(addDays(from, deltaWeeks * 7));
}

export function formatWeekInputValue({ isoYear, isoWeek }: IsoWeek): string {
  return `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
}

function parseYearMonth(yearMonth: string): { y: number; m: number } {
  const [y, m] = yearMonth.split('-', 2);
  return { y: Number(y), m: Number(m) };
}

export function parseWeekInputValue(value: string): IsoWeek {
  const [y, w] = value.split('-W', 2);
  return { isoYear: Number(y), isoWeek: Number(w) };
}

export function monthRange(yearMonth: string): { from: string; to: string } {
  const { y, m } = parseYearMonth(yearMonth);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${yearMonth}-01`, to: `${yearMonth}-${String(lastDay).padStart(2, '0')}` };
}

export function shiftMonth(yearMonth: string, delta: number): string {
  const { y, m } = parseYearMonth(yearMonth);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function yearRange(year: number): { from: string; to: string } {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export interface StatTotals {
  visitors: number;
  sessions: number;
  pageViews: number;
  conversions: number;
  phoneClicks: number;
  whatsappClicks: number;
}

const emptyTotals: StatTotals = { visitors: 0, sessions: 0, pageViews: 0, conversions: 0, phoneClicks: 0, whatsappClicks: 0 };

export function sumStats(points: DailyStatPoint[]): StatTotals {
  return points.reduce(
    (acc, p) => ({
      visitors: acc.visitors + p.visitors,
      sessions: acc.sessions + p.sessions,
      pageViews: acc.pageViews + p.pageViews,
      conversions: acc.conversions + p.conversions,
      phoneClicks: acc.phoneClicks + p.phoneClicks,
      whatsappClicks: acc.whatsappClicks + p.whatsappClicks,
    }),
    { ...emptyTotals },
  );
}

export interface MonthBucket extends StatTotals {
  month: string; // YYYY-MM
}

export function groupByMonth(points: DailyStatPoint[]): MonthBucket[] {
  const byMonth = new Map<string, DailyStatPoint[]>();
  for (const p of points) {
    const month = p.date.slice(0, 7);
    const bucket = byMonth.get(month);
    if (bucket) bucket.push(p);
    else byMonth.set(month, [p]);
  }
  return [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, pts]) => ({ month, ...sumStats(pts) }));
}

const WEEKDAY_LABELS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function weekdayLabel(dateStr: string): string {
  const d = fromIsoDate(dateStr);
  return WEEKDAY_LABELS_DE[(d.getUTCDay() + 6) % 7]!;
}

const MONTH_LABELS_DE = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

export function monthLabel(yearMonth: string): string {
  const { m } = parseYearMonth(yearMonth);
  return MONTH_LABELS_DE[m - 1]!;
}

export function formatShortDate(dateStr: string): string {
  const d = fromIsoDate(dateStr);
  return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.`;
}
